import React from 'react';
import { Plus, ShoppingBag } from 'lucide-react';
import { getItemImageSrc } from '../utils/itemImage';
import { useLanguage } from '../context/LanguageContext';
import { itemLabel } from '../utils/itemName';

// quantityOnToken is how many of this item sit on the token being served right
// now - not the shop-wide pending count, which belongs to every customer at once
function ItemCard({ item, onAddOrder, quantityOnToken = 0 }) {
  const { t, language } = useLanguage();
  const name = itemLabel(item, language);
  const outOfStock = item.isInventoryItem && item.stock === 0;
  const lowStock = item.isInventoryItem && !outOfStock && item.stock <= item.lowStockLimit;

  const handleAddOrder = () => {
    onAddOrder(item);
  };

  return (
    <div
      className={`bg-white rounded-2xl border overflow-hidden flex flex-col transition-shadow duration-200 ${
        outOfStock
          ? 'border-gray-200 opacity-60'
          : 'border-gray-100 shadow-sm hover:shadow-md'
      }`}
    >
      {/* Image - full bleed so the card reads as one calm surface */}
      <div
        className="relative h-24 sm:h-32 bg-cafe-50 cursor-pointer"
        onClick={handleAddOrder}
      >
        {item.image ? (
          <img
            src={getItemImageSrc(item.image)}
            alt={name}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ShoppingBag className="h-8 w-8 sm:h-10 sm:w-10 text-cafe-300" />
          </div>
        )}

        {quantityOnToken > 0 && (
          <span className="absolute top-1.5 right-1.5 min-w-[22px] h-[22px] px-1.5 flex items-center justify-center bg-cafe-600 text-white text-xs font-bold rounded-full shadow-sm">
            {quantityOnToken}
          </span>
        )}
      </div>

      {/* Info */}
      <div className="flex-1 p-2.5 sm:p-3.5 flex flex-col">
        <h3 className="text-sm sm:text-base font-semibold text-gray-900 truncate">
          {name}
        </h3>

        <div className="mt-0.5 mb-2.5 sm:mb-3 flex items-baseline justify-between gap-1.5">
          <span className="text-base sm:text-lg font-bold text-cafe-700">
            Rs {item.price}
          </span>
          <span className="text-[11px] sm:text-xs text-gray-400 truncate">
            {(item.soldToday || 0) > 0 && t('{count} sold', { count: item.soldToday })}
          </span>
        </div>

        {/* One quiet meta line instead of stat boxes - keeps cards short so
            more of the menu fits on a phone screen */}
        {item.isInventoryItem && (
          <div className="mb-2.5 sm:mb-3 flex items-center gap-1.5 text-xs font-medium">
            <span
              className={`h-1.5 w-1.5 rounded-full flex-shrink-0 ${
                outOfStock ? 'bg-red-500' : lowStock ? 'bg-amber-500' : 'bg-green-500'
              }`}
            />
            <span className={outOfStock ? 'text-red-600' : lowStock ? 'text-amber-600' : 'text-gray-500'}>
              {outOfStock ? t('Out of stock') : t('{count} in stock', { count: item.stock })}
            </span>
          </div>
        )}

        <button
          onClick={handleAddOrder}
          disabled={outOfStock}
          className="mt-auto w-full min-h-[44px] rounded-xl font-semibold text-sm flex items-center justify-center gap-1.5 bg-cafe-600 text-white hover:bg-cafe-700 active:scale-[0.97] transition-all focus:outline-none focus:ring-2 focus:ring-cafe-500 focus:ring-offset-2 disabled:bg-gray-100 disabled:text-gray-400 disabled:active:scale-100"
        >
          <Plus className="h-4 w-4 flex-shrink-0" />
          <span className="truncate">{outOfStock ? t('Out of Stock') : t('Add')}</span>
        </button>
      </div>
    </div>
  );
}

export default ItemCard;
